import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import NotificationBell from "@/components/NotificationBell";
import OfflineBanner from "@/components/layout/OfflineBanner";
import GlobalSearch from "@/components/GlobalSearch";
import { CaretLeft, Robot, Broadcast, MagnifyingGlass } from "@phosphor-icons/react";

export default function TopHeader({ title }) {
  const nav = useNavigate();
  const loc = useLocation();
  const { user } = useAuth();
  const [searchOpen, setSearchOpen] = useState(false);
  const showBack = loc.pathname.startsWith("/chantiers/") && loc.pathname !== "/chantiers";

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const initials = (user?.name || "?")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <header
      data-testid="top-header"
      className="sticky top-0 z-20 glass-dark border-b border-white/10 pt-safe"
    >
      <div className="h-14 flex items-center justify-between px-4">
        <div className="flex items-center gap-3 min-w-0">
          {showBack ? (
            <button
              onClick={() => nav(-1)}
              className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center active:scale-95"
              data-testid="header-back-btn"
              title="Retour"
            >
              <CaretLeft size={18} weight="bold" />
            </button>
          ) : (
            <button
              onClick={() => nav("/")}
              className="w-9 h-9 rounded-xl bg-[#005CFF] flex items-center justify-center shadow-[0_0_20px_rgba(0,92,255,0.4)] active:scale-95 transition-transform"
              data-testid="header-home-btn"
              title="Tableau de bord"
              aria-label="Tableau de bord"
            >
              <span className="font-display font-extrabold text-sm text-white leading-none">B</span>
            </button>
          )}
          <div className="min-w-0">
            <div className="font-display font-bold text-base truncate">{title}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 leading-tight">BKflow</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSearchOpen(true)}
            className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center active:scale-95"
            data-testid="header-search-btn"
            aria-label="Recherche globale"
            title="Recherche (⌘K)"
          >
            <MagnifyingGlass size={18} />
          </button>
          <button
            onClick={() => nav("/fil")}
            className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center active:scale-95"
            data-testid="header-feed-btn"
            aria-label="Fil d'actualité"
          >
            <Broadcast size={18} />
          </button>
          <NotificationBell />
          <button
            onClick={() => nav("/ia")}
            className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center active:scale-95"
            data-testid="header-ai-btn"
            aria-label="Assistant IA"
          >
            <Robot size={18} />
          </button>
          <div
            className="w-9 h-9 rounded-full bg-gradient-to-br from-[#005CFF] to-[#0047CC] flex items-center justify-center text-xs font-bold"
            data-testid="header-avatar"
          >
            {initials}
          </div>
        </div>
      </div>
      <OfflineBanner />
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
}
